import React, { useCallback, useState } from 'react'
import { Platform } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { publishEvent } from '@zet/analytics'
import LazyHOC from '@zet/common/components/lazyHoc'
import { MotiView } from '@zet/common/components/motiView'
import {
  B1,
  B2,
  B4,
  B5,
  BottomSheet,
  Button,
  colors,
  H4,
  H5,
  Icon,
  Input,
  SH2,
  SH3,
  Stack,
  XStack,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { i18n } from '@zet/localization'
import { KeyBoardWrapper } from '@zet/utils'
import { MOBILE_NUMBER_REGEX } from '@zet/utils/regularExpression'
import { useUnsafeVariable } from 'nativewind'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import NoAccess from '../../../assets/svgs/noAccess.svg'
import PhoneRoundOutlined from '../../../assets/svgs/phoneRoundOutlined.svg'
import { IRequestData } from '../../api/functions/customerAdditionStateMachine'
import {
  useAddCustomerNextState,
  useAddCustomerNextStateWeb,
} from '../../api/hooks'
import useStore from '../../store'

const isWeb = Platform.OS === 'web'

const VerifySecondaryPhoneNumber = ({
  route: {
    params: { customerId, maskedMobileNumbers = [] },
  },
}: {
  route: {
    params: {
      customerId: number
      maskedMobileNumbers: Array<string>
    }
  }
}) => {
  const [colorPrimary100] = useUnsafeVariable('--color-primary-100')
  const [colorGrays50] = useUnsafeVariable('--color-helpAndSupport-gray150')
  const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false)
  const [showAll, setShowAll] = useState<boolean>(false)

  const { setSelectedCustomer, selectedCustomer } = useStore((state) => state)

  const { mutate: mutateWithExperiments, isLoading: isExperimentsLoading } =
    useAddCustomerNextState()
  const { mutate: mutateWithExperimentsForWeb, isLoading: experimentLoading } =
    useAddCustomerNextStateWeb()

  type ValidationSchema = z.infer<typeof validationSchema>

  const validationSchema = z.object({
    secondaryPhoneNumber: z
      .string()
      .min(1, { message: 'Phone number is required' })
      .regex(
        new RegExp(MOBILE_NUMBER_REGEX),
        'The entered number does not match the required format'
      )
      .max(10, { message: 'Phone number cannot exceed 10 digits' })
      .refine(
        (value) => {
          // Extract first two digits and last three digits of the input
          const firstTwoDigits = value.slice(0, 2)
          const lastThreeDigits = value.slice(-3)

          // Check if any number in the array matches the first two and last three digits
          return maskedMobileNumbers?.some(
            (number) =>
              number.startsWith(firstTwoDigits) &&
              number.endsWith(lastThreeDigits)
          )
        },
        {
          message: 'The entered number does not match the list',
        }
      ),
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    getValues,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })

  const closeBottomSheet = useCallback(() => {
    setShowBottomSheet(false)
  }, [])

  const numbersToDisplay = showAll
    ? maskedMobileNumbers
    : maskedMobileNumbers?.slice(0, 3)

  const onSubmit: SubmitHandler<{ secondaryPhoneNumber: string }> = async ({
    secondaryPhoneNumber,
  }) => {
    if (isWeb) {
      const urlParams = new URLSearchParams(window.location.search)
      const hash = urlParams.get('hashNew')
      const cusId = urlParams.get('customerId')
      const requestBodyData = {
        hash,
        customerId: customerId || cusId,
        secondaryPhoneNumber: secondaryPhoneNumber?.trim()
          ? String(secondaryPhoneNumber)
          : null,
        screenName: 'SHOWN_SECONDARY_PHONE_NUMBER_SCREEN',
      }
      await mutateWithExperimentsForWeb(requestBodyData as IRequestData)
    } else {
      const requestBodyData = {
        secondaryPhoneNumber: secondaryPhoneNumber?.trim()
          ? String(secondaryPhoneNumber)
          : null,
        customerId: customerId,
        screenName: 'SHOWN_SECONDARY_PHONE_NUMBER_SCREEN',
      }
      await mutateWithExperiments(requestBodyData as IRequestData)
      publishEvent('click_action', {
        item_name: 'verify_secondary_phone_number',
        customer_id: customerId,
      })
    }
    closeBottomSheet()
    setSelectedCustomer({
      ...selectedCustomer,
      secondaryPhoneNumber,
    })
  }

  return (
    <LazyHOC>
      <KeyBoardWrapper
        className="flex-1 bg-white"
        BottomSheet={
          <Stack className="border-t-grays-30 border-t px-3 pt-3">
            <Button
              size={'lg'}
              className={'w-full'}
              style={{
                backgroundColor:
                  isValid || isExperimentsLoading
                    ? colorPrimary100
                    : colorGrays50,
              }}
              textStyle={'text-base'}
              disabled={!isValid || isExperimentsLoading || experimentLoading}
              loading={isExperimentsLoading || experimentLoading}
              onPress={handleSubmit(onSubmit)}
              loaderText={i18n.t('verifySecondaryPhNum.verifying')}
            >
              {i18n.t('verifySecondaryPhoneNumber.sendOtp')}
            </Button>
            <Button
              size={'lg'}
              outlined={true}
              className={'my-2 w-full '}
              onPress={() => setShowBottomSheet(true)}
              textStyle={'text-primary-100 text-base'}
            >
              {i18n.t('verifySecondaryPhoneNumber.dontHaveAccesstoNumbers')}{' '}
            </Button>
          </Stack>
        }
      >
        {/* Header */}
        <Stack className="bg-white px-3 py-2">
          <H5 className="text-grays-100 font-[Graphie-Bold]">
            {isWeb
              ? i18n.t('webFlow.verifySecondaryPhoneNumber.verifyPhoneNumber')
              : i18n.t('verifySecondaryPhoneNumber.verifyPhoneNumber')}
          </H5>
          <B1 className="text-grays-70 my-2">
            {isWeb
              ? i18n.t(
                  'webFlow.verifySecondaryPhoneNumber.foundMultuplePhoneNumber'
                )
              : i18n.t('verifySecondaryPhoneNumber.foundMultuplePhoneNumber')}
          </B1>
        </Stack>

        {/* Numbers linked */}
        <Stack className="my-3 px-3">
          <Stack className="border-grays-40 rounded-lg border-[1px] p-3">
            <SH3 className="text-grays-80 mb-2">
              {isWeb
                ? i18n.t('webFlow.verifySecondaryPhoneNumber.numbersLinked')
                : i18n.t('verifySecondaryPhoneNumber.numbersLinked')}
            </SH3>
            <Stack className="mt-2">
              {numbersToDisplay.map((number, index) => {
                return (
                  <MotiView
                    key={index}
                    from={{ opacity: !showAll ? 1 : 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ type: 'timing', duration: 300 }}
                  >
                    <XStack className="bg-grays-10 mb-1 items-center p-2">
                      <PhoneRoundOutlined width={18} height={18} />
                      <SH2 className="pl-2 text-[#141216]">{number}</SH2>
                    </XStack>
                  </MotiView>
                )
              })}
            </Stack>
            <XStack className="mt-2 items-center justify-between">
              <B5 className="text-grays-60 mt-1 ">
                {i18n.t('verifySecondaryPhoneNumber.total')}{' '}
                {maskedMobileNumbers?.length}{' '}
                {i18n.t('verifySecondaryPhoneNumber.numbersFound')}
              </B5>
              {maskedMobileNumbers?.length > 3 && !showAll ? (
                <Pressable
                  hitSlop={{ top: 10, bottom: 10, left: 10 }}
                  onPress={() => setShowAll(!showAll)}
                  className="flex-row items-center"
                >
                  <B4 className="text-primary-100 text-xs">
                    {i18n.t('verifySecondaryPhoneNumber.seeMore')}
                  </B4>
                  <Icon
                    type={showAll ? 'ArrowTop' : 'ArrowDown'}
                    size={22}
                    color={colors.primary[100]}
                    className="ml-2"
                  />
                </Pressable>
              ) : null}
            </XStack>
          </Stack>
          <Stack className="my-4">
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Eg: 9910687765"
                  onBlur={onBlur}
                  onChangeText={(value) => {
                    onChange(value)
                  }}
                  keyboardType="numeric"
                  label={i18n.t('verifySecondaryPhoneNumber.enterLinkedNumber')}
                  isRequired
                  value={value}
                  type={
                    (getValues()?.secondaryPhoneNumber?.trim()?.length === 0 &&
                      !touchedFields.secondaryPhoneNumber) ||
                    getValues()?.secondaryPhoneNumber === undefined
                      ? 'primary'
                      : !isValid
                      ? 'error'
                      : 'success'
                  }
                  maxLength={10}
                  iconRight={
                    getValues()?.secondaryPhoneNumber?.length === 0 ? (
                      <Icon
                        color={colors.error[100]}
                        type="ErrorOutlined"
                        size={28}
                        className="mr-2"
                      />
                    ) : typeof getValues()?.secondaryPhoneNumber ===
                      'undefined' ? null : !isValid ? (
                      <Icon
                        color={colors.error[100]}
                        type="ErrorOutlined"
                        size={28}
                        className="mr-2"
                      />
                    ) : (
                      <Icon
                        color={colors.success[100]}
                        type="DoneOutlined"
                        size={28}
                        className="mr-2"
                      />
                    )
                  }
                />
              )}
              name="secondaryPhoneNumber"
            />
            {errors.secondaryPhoneNumber?.message ? (
              <Stack className="-mt-3">
                <B5 className="text-error-100">
                  {errors.secondaryPhoneNumber?.message}
                </B5>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </KeyBoardWrapper>
      <BottomSheet
        visible={showBottomSheet}
        sheetName="choose_slot_bottom_sheet"
        onDismiss={closeBottomSheet}
      >
        <Stack className="pt-3">
          <Stack className="flex items-center justify-center px-4">
            <Stack>
              <NoAccess width={75} height={90} />
            </Stack>
            <H4 className="text-grays-100 text-center font-[Graphie-Bold]">
              {i18n.t('verifySecondaryPhoneNumber.dontHaveAccess')}
            </H4>
            <B2 className="text-grays-60 mt-2 text-center">
              {i18n.t('verifySecondaryPhoneNumber.pleaseNote')}
            </B2>
          </Stack>
          <Stack className="p-3">
            <Button
              size={'lg'}
              className={'w-full'}
              textStyle="text-base"
              style={{
                backgroundColor: colorPrimary100,
              }}
              loading={isExperimentsLoading || experimentLoading}
              onPress={() => {
                onSubmit({ secondaryPhoneNumber: '' })
              }}
              loaderText={i18n.t('verifySecondaryPhNum.verifying')}
            >
              {i18n.t('verifySecondaryPhoneNumber.yesSure')}
            </Button>
            <Button
              size={'lg'}
              className={'mt-2.5 w-full'}
              outlined
              textStyle="text-primary-100 text-base"
              onPress={closeBottomSheet}
            >
              {i18n.t('verifySecondaryPhoneNumber.noBack')}
            </Button>
          </Stack>
        </Stack>
      </BottomSheet>
    </LazyHOC>
  )
}

export default VerifySecondaryPhoneNumber
